/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */

import { Button, Center, Loader, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconAdjustmentsAlt, IconDownload, IconTrash, IconUpload } from '@tabler/icons-react';
import moment from 'moment';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { TSDListItem } from '@e-origin/contracts';
import { EORIGIN_DATE_FORMAT_WITH_TIME, TSDCriteria, TSDType } from '@e-origin/shared';

import { ChangeTsdStatus } from '@admin/components/ManageTSDs/ChangeTSDStatus';
import { getInitialAdminTSDCriteria, prefetchAdminTSDs, useAdminDeleteTsd, useAdminExportTsd, useAdminTSDCriteriaUrlSync, useAdminTSDs } from '@admin/hooks';
import { SearchBox, Table, TableActions, TableColumnProps } from '@components';
import { BATCH_LIST_NEXT_PAGE } from '@constants';
import { ContentLayout } from '@features/layout';
import { TSDTypeView } from '@features/tsd/TSDList/TSDTypeView';
import { getStatusLabel } from '@features/tsd/utils';
import { removeUndefined } from '@utils';
import { ImportTsd } from './ImportTSD';

export const ManageTSDs = () => {
  const overflowEllipsisStyle = {
    maxWidth: '350px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textWrapMode: 'nowrap',
  };

  const theme = useMantineTheme();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialAdminTSDCriteria(searchParams);
  const exportTsdMutation = useAdminExportTsd();
  const [filters, setFilters] = useState<TSDCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<TSDCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<TSDCriteria['pagination']>(initialCriteria.pagination);
  const [tsdView, setTsdView] = useState<TSDType>(initialCriteria.filters.type ?? TSDType.COMBINED);
  const deleteTsdMutation = useAdminDeleteTsd();
  useAdminTSDCriteriaUrlSync({ filters, sorting, pagination });

  const { data: { list: tsdsList, total: totalTsdCount } = { list: [], total: 0 }, isLoading } = useAdminTSDs({
    filters,
    sorting,
    pagination,
  });

  const setTsdFilters = (updatedFilters: Partial<TSDCriteria['filters']>) => {
    const mergedFilters = removeUndefined({
      ...filters,
      ...updatedFilters,
    });

    setFilters(mergedFilters);
  };

  const prefetchNextPage = (page: number) => prefetchAdminTSDs({ filters, sorting, pagination: { ...pagination, page } });

  const searchTsds = (searchQuery: string) => {
    setTsdFilters({ searchQuery });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
    prefetchNextPage(page + 1);
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const openChangeTsdStatusModal = (tsd: TSDListItem) => {
    modals.open({
      title: 'Change TSD Status',
      children: <ChangeTsdStatus onClose={() => modals.closeAll()} tsd={tsd} />,
      size: 'lg',
      centered: true,
    });
  };

  const openImportTsdModal = () => {
    let modalTitle = 'Import TSD';

    if (tsdView) {
      switch (tsdView) {
        case TSDType.PRELODGED:
          modalTitle = 'Import Pre-lodged TSD';
          break;
        case TSDType.COMBINED:
          modalTitle = 'Import Combined TSD';
          break;
        case TSDType.TRANSFERRED:
          modalTitle = 'Import Transferred TSD';
          break;
        default:
          modalTitle = 'Import TSD';
      }
    }

    modals.open({
      title: modalTitle,
      children: <ImportTsd onClose={() => modals.closeAll()} tsdView={tsdView} />,
      size: 'lg',
      centered: true,
    });
  };

  const handleTSDType = (type: TSDType) => {
    setTsdView(type);

    setTsdFilters({
      type,
    });

    setPagination({
      page: 1,
      size: pagination.size,
    });

    prefetchNextPage(BATCH_LIST_NEXT_PAGE);
  };

  const openDeleteTsdModal = (tsd: TSDListItem) => {
    modals.openConfirmModal({
      title: 'Delete Temporary Storage',
      children: <Text>Are you sure you want to delete this temporary storage? This action cannot be undone!</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteTsdMutation.mutate(tsd._id),
    });
  };
  const columns: TableColumnProps<TSDListItem>[] = [
    {
      name: 'TSD ID',
      selector: 'counter',
      width: '120px',
      cell: (row) => row.counter,
      sortable: true,
    },
    {
      name: 'TSD name',
      selector: 'name',
      sortable: true,
      cell: (row) => row.name,
      style: overflowEllipsisStyle,
    },
    {
      name: 'TSD status',
      selector: 'generalInfo.status',
      sortable: true,
      cell: (row) => getStatusLabel(row.generalInfo.status),
      style: overflowEllipsisStyle,
    },
    {
      name: 'LRN',
      selector: 'messageInfo.LRN',
      cell: (row) => row.messageInfo?.LRN,
      style: overflowEllipsisStyle,
    },
    {
      name: 'MRN',
      selector: 'messageInfo.MRN',
      cell: (row) => row.messageInfo?.MRN,
      style: overflowEllipsisStyle,
    },
    {
      name: 'Date',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      selector: 'updatedAt',
      sortable: true,
    },
    {
      name: 'Template name',
      selector: 'template.name',
      cell: (row) => (row.template as any)?.name,
    },
    {
      name: 'Template version',
      selector: 'template.version',
      cell: (row) => (row.template as any)?.version,
    },
    {
      name: 'Template _id',
      selector: 'template._id',
      cell: (row) => (row.template as any)?._id,
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => {
        const secondaryActionsList = [
          {
            iconComponent: <IconAdjustmentsAlt color={theme.colors[theme.primaryColor][6]} />,
            text: 'Change TSD Status',
            onClick: () => openChangeTsdStatusModal(row),
          },
          {
            iconComponent: <IconDownload color={theme.colors[theme.primaryColor][6]} />,
            text: 'Export TSD',
            onClick: () => exportTsdMutation.mutate(row._id),
            loading: exportTsdMutation.isPending,
          },
          {
            iconComponent: <IconTrash color={theme.colors.red[6]} />,
            text: 'Delete TSD',
            onClick: () => openDeleteTsdModal(row),
          },
        ];

        return <TableActions actions={[]} secondaryActions={secondaryActionsList} showDebugMenu={false} />;
      },
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage TSDs</ContentLayout.Title>

        <ContentLayout.Filters>
          <Button leftSection={<IconUpload size={16} />} onClick={openImportTsdModal}>
            Import TSD
          </Button>
          <TSDTypeView value={tsdView} onChange={handleTSDType} />
          <SearchBox handleSearch={searchTsds} value={filters?.searchQuery} placeholder="Search by counter, name, LRN, MRN, CRN or FRN" />
        </ContentLayout.Filters>
      </ContentLayout.Header>

      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={tsdsList ?? []}
          total={totalTsdCount}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
